import { jsPDF } from 'jspdf';
import { bisaCode128, jumlahModulCode128, modulCode128 } from './code128';

// Label barcode produk (PDF vektor): nama, harga, barcode Code 128 dari kode barang, dan kode. Ukuran & susunan siap pakai
// untuk lembar stiker A4 dan printer label.
export const UKURAN_LABEL = {
  'a4-3x8': { nama: 'A4 • 3 × 8 (70 × 37 mm)', halaman: 'a4', kolom: 3, baris: 8, lebar: 70, tinggi: 37, marginX: 0, marginY: 0.5 },
  'a4-4x13': { nama: 'A4 • 4 × 13 (48,5 × 21,2 mm)', halaman: 'a4', kolom: 4, baris: 13, lebar: 48.5, tinggi: 21.2, marginX: 8, marginY: 10.7 },
  'satu-50x30': { nama: 'Printer label • 50 × 30 mm (1 per halaman)', halaman: [50, 30], kolom: 1, baris: 1, lebar: 50, tinggi: 30, marginX: 0, marginY: 0 },
};

// Batas agar halaman tidak macet / PDF tidak raksasa (mis. stok 100.000 tidak boleh jadi 100.000 label).
export const MAKS_PER_PRODUK = 500;
export const MAKS_TOTAL_LABEL = 2000;

const MM_PER_PT = 25.4 / 72;
const ZONA_HENING = 10; // modul kosong di kiri & kanan barcode
const MODUL_MIN_MM = 0.19; // lebih rapat dari ini sulit dipindai scanner biasa

export const rupiahLabel = (v) => `Rp ${Math.round(Number(v) || 0).toLocaleString('id-ID')}`;

/** Ringkasan rencana cetak: jumlah label, halaman, dan label yang barcodenya terlalu rapat/tidak valid. */
export const rencanaLabel = (daftar, ukuranKey, lewati = 0) => {
  const u = UKURAN_LABEL[ukuranKey];
  const perHalaman = u.kolom * u.baris;
  const jumlah = daftar.reduce((a, d) => a + Math.max(0, Math.floor(Number(d.jumlah) || 0)), 0);
  const halaman = jumlah === 0 ? 0 : Math.ceil((jumlah + Math.max(0, lewati)) / perHalaman);
  const pad = Math.min(2, u.lebar * 0.04);
  const rapat = daftar.filter((d) => bisaCode128(d.kode) && (u.lebar - 2 * pad) / (jumlahModulCode128(d.kode) + 2 * ZONA_HENING) < MODUL_MIN_MM);
  const takValid = daftar.filter((d) => !bisaCode128(d.kode));
  return { jumlah, halaman, perHalaman, rapat, takValid, terlalu_banyak: jumlah > MAKS_TOTAL_LABEL };
};

const potong = (doc, teks, lebarMax) => {
  if (doc.getTextWidth(teks) <= lebarMax) return teks;
  let t = teks;
  while (t.length > 1 && doc.getTextWidth(`${t}…`) > lebarMax) t = t.slice(0, -1);
  return `${t.trimEnd()}…`;
};

const gambarLabel = (doc, x, y, w, h, item, opsi) => {
  const pad = Math.min(2, w * 0.04);
  const isiW = w - 2 * pad;
  if (opsi.garis) {
    doc.setDrawColor(190);
    doc.setLineWidth(0.1);
    doc.rect(x, y, w, h);
  }
  doc.setTextColor(0);
  let cy = y + pad;

  const tinggiNama = opsi.nama ? h * 0.2 : 0;
  const tinggiHarga = opsi.harga ? h * 0.2 : 0;
  const tinggiKode = h * 0.14;
  if (opsi.nama) {
    const pt = Math.min(11, (tinggiNama / MM_PER_PT) * 0.85);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(pt);
    doc.text(potong(doc, item.nama, isiW), x + w / 2, cy + tinggiNama * 0.78, { align: 'center' });
    cy += tinggiNama;
  }
  if (opsi.harga) {
    const pt = Math.min(11, (tinggiHarga / MM_PER_PT) * 0.85);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(pt);
    doc.text(rupiahLabel(item.harga), x + w / 2, cy + tinggiHarga * 0.78, { align: 'center' });
    cy += tinggiHarga;
  }
  const tinggiBar = Math.max(4, y + h - pad - tinggiKode - cy);
  if (bisaCode128(item.kode)) {
    const modul = modulCode128(item.kode);
    const lebarModul = isiW / (modul.length + 2 * ZONA_HENING);
    const x0 = x + pad + ZONA_HENING * lebarModul;
    doc.setFillColor(0, 0, 0);
    let i = 0;
    while (i < modul.length) {
      if (modul[i] === 1) {
        let j = i;
        while (j < modul.length && modul[j] === 1) j += 1;
        doc.rect(x0 + i * lebarModul, cy, (j - i) * lebarModul, tinggiBar, 'F');
        i = j;
      } else {
        i += 1;
      }
    }
  } else {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7);
    doc.text('Kode tidak bisa dibuat barcode', x + w / 2, cy + tinggiBar / 2, { align: 'center' });
  }
  doc.setFont('courier', 'normal');
  const ptKode = Math.min(9, (tinggiKode / MM_PER_PT) * 0.9);
  doc.setFontSize(ptKode);
  doc.text(potong(doc, item.kode, isiW), x + w / 2, y + h - pad - tinggiKode * 0.2, { align: 'center' });
};

/** Ambil paling banyak `n` label pertama (untuk pratinjau satu halaman saja, supaya cepat). */
export const potongLabel = (daftar, n) => {
  const hasil = [];
  let sisa = n;
  for (const d of daftar) {
    if (sisa <= 0) break;
    const j = Math.min(Math.max(0, Math.floor(Number(d.jumlah) || 0)), sisa);
    if (j > 0) hasil.push({ ...d, jumlah: j });
    sisa -= j;
  }
  return hasil;
};

/**
 * Label yang jatuh di halaman ke-`halaman` (mulai 1), termasuk label kosong di awal (`lewati`) pada halaman itu.
 * @returns {{ daftar: Array, lewati: number }}
 */
export const halamanLabel = (daftar, perHalaman, lewati, halaman) => {
  const awal = (Math.max(1, halaman) - 1) * perHalaman;
  const akhir = awal + perHalaman;
  const kosong = Math.max(0, Math.floor(lewati) || 0);
  const dari = Math.max(0, awal - kosong);
  const sampai = Math.max(0, akhir - kosong);
  const hasil = [];
  let pos = 0;
  for (const d of daftar) {
    const j = Math.max(0, Math.floor(Number(d.jumlah) || 0));
    const a = Math.max(dari, pos);
    const b = Math.min(sampai, pos + j);
    if (b > a) hasil.push({ ...d, jumlah: b - a });
    pos += j;
  }
  return { daftar: hasil, lewati: Math.min(perHalaman, Math.max(0, kosong - awal)) };
};

/**
 * Buat dokumen PDF label.
 * @param {Array<{nama:string, harga:number, kode:string, jumlah:number}>} daftar
 * @param {string} ukuranKey kunci UKURAN_LABEL
 * @param {{nama?:boolean, harga?:boolean, garis?:boolean, lewati?:number}} opsi  lewati = jumlah label pertama yang dikosongkan
 */
export const buatPdfLabel = (daftar, ukuranKey, opsi = {}) => {
  const u = UKURAN_LABEL[ukuranKey];
  const tampil = { nama: opsi.nama !== false, harga: opsi.harga !== false, garis: !!opsi.garis };
  const doc = new jsPDF({ unit: 'mm', format: u.halaman, orientation: 'portrait' });
  const perHalaman = u.kolom * u.baris;
  const semua = [];
  for (let i = 0; i < Math.max(0, Math.floor(opsi.lewati || 0)); i += 1) semua.push(null);
  daftar.forEach((d) => {
    for (let i = 0; i < Math.max(0, Math.floor(Number(d.jumlah) || 0)); i += 1) semua.push(d);
  });
  semua.forEach((item, idx) => {
    const posisi = idx % perHalaman;
    if (idx > 0 && posisi === 0) doc.addPage(u.halaman, 'portrait');
    if (!item) return;
    const kol = posisi % u.kolom;
    const bar = Math.floor(posisi / u.kolom);
    gambarLabel(doc, u.marginX + kol * u.lebar, u.marginY + bar * u.tinggi, u.lebar, u.tinggi, item, tampil);
  });
  return doc;
};
